import { useQuery } from '@tanstack/react-query';
import { Col, Collapse, CollapseProps, Divider, Flex, Form, Row, Skeleton, Typography } from 'antd';
import Parser from 'html-react-parser';
import { Fragment, useContext, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getHandoverTicket } from 'src/apis/office-ticket/handoverTicket.api';
import BtnOfficeHandle from 'src/components/v2/Button/BtnOfficeHandle';
import { HRTicketStatusTag } from 'src/components/v2/Tag/TicketStatusTag/TicketStatusTag';
import { OfficeTicketDetailContext } from 'src/contexts/officeTicketDetail.context';
import { TicketContext } from 'src/contexts/ticket.context';
import { IHandoverItem } from 'src/types/office_ticket/handoverTicket.type';
import { formatDateTime } from 'src/utils/utils';
import './HandoverTicketDetail.scss';
const { Title, Text } = Typography;

export default function HandoverTicketDetail() {
  const { setInvalidKey } = useContext(TicketContext);
  const { setHandoverTicket, setLoading } = useContext(OfficeTicketDetailContext);
  const { id } = useParams();
  const { data, isFetching } = useQuery({
    queryKey: ['handoverTicket', id],
    queryFn: () => getHandoverTicket(Number(id)),
    enabled: !!id
  });
  useEffect(() => {
    if (data) {
      setHandoverTicket(data);
      setLoading(isFetching);
      setInvalidKey(JSON.stringify(data?.data));
    }
  }, [data, isFetching, setHandoverTicket, setInvalidKey, setLoading]);

  const handoverTicketDetailItems = (handoverTicketDetails: IHandoverItem[]): CollapseProps['items'] => {
    return handoverTicketDetails.map((handoverTicketDetail) => ({
      key: handoverTicketDetail.id,
      label: (
        <Flex className='handover-ticket-item-header' vertical>
          <Form.Item label='Mã'>
            <Text strong>{handoverTicketDetail.itemNo}</Text>
          </Form.Item>
          <Form.Item label='Tên'>
            <Text strong>{handoverTicketDetail.itemName}</Text>
          </Form.Item>
          <Form.Item label='Số lượng'>
            <Text>{handoverTicketDetail.quantity}</Text>
          </Form.Item>
          <Form.Item label='Mô tả'>
            <Text>{handoverTicketDetail.description}</Text>
          </Form.Item>
        </Flex>
      ),
      children: (
        <Flex vertical>
          <Form.Item label='Ghi chú' style={{ marginBottom: 0 }}>
            <Text>{handoverTicketDetail.remarks}</Text>
          </Form.Item>
        </Flex>
      ),
      className: 'handover-ticket-item'
    }));
  };

  return (
    <div className='handover-ticket-detail-wrapper'>
      <BtnOfficeHandle type='handoverTicket' />

      <Divider style={{ marginTop: 16, marginBottom: 16 }} />
      <Fragment>
        <Flex justify='start'>
          <Title level={5}>Thông tin phiếu</Title>
        </Flex>
        {isFetching ? (
          <Skeleton active />
        ) : (
          <Fragment>
            <Form layout='horizontal' labelCol={{ flex: '150px' }} labelAlign='left'>
              <Row wrap={true} gutter={[20, 0]}>
                <Col span={12}>
                  <Form.Item label='Mã phiếu'>
                    <Text strong>{data?.data?.code}</Text>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item label='Trạng thái'>
                    <HRTicketStatusTag ticketStatus={data?.data?.ticketStatus} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label='Người giao'>{data?.data?.senderBy?.fullName}</Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label='Bộ phận'>{data?.data?.senderBy?.department?.name}</Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label='Người nhận'>{data?.data?.receiverBy?.fullName}</Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label='Bộ phận'>{data?.data?.receiverBy?.department?.name}</Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label='Người tạo'>{data?.data?.createdBy?.fullName}</Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label='Bộ phận'>{data?.data?.createdBy?.department?.name}</Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label='Ngày tạo'>{formatDateTime(data?.data.createdDate)}</Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item label='Lý do bàn giao'>{Parser(data?.data?.note || '')}</Form.Item>
                </Col>
              </Row>
            </Form>
            <Divider style={{ marginTop: 16, marginBottom: 16 }} />
            <Flex justify='start'>
              <Title level={5}>Danh sách công cụ, dụng cụ</Title>
            </Flex>
            <Form layout='horizontal' labelCol={{ flex: '135px' }} labelAlign='left'>
              <Collapse
                bordered={false}
                items={handoverTicketDetailItems(data?.data.handOverTicketDetails || [])}
                expandIconPosition='end'
                className='handover-ticket-list'
              />
            </Form>
          </Fragment>
        )}
      </Fragment>
    </div>
  );
}
